import type {
  DealIntelligenceProject,
  GapDraftView,
  GapProjectOverview,
} from '@momentum/types';

import type { DetailGap } from '@/features/deal-intelligence/components/DealIntelligencePage';
import { ENRICHMENT_LABEL, OVERVIEW_LABEL } from './agents';
import { titleOf, titleOrigin } from './derive-title';
import {
  resolveActionItems,
  resolveClaim,
  resolveEscalation,
  resolveFullProjectContext,
  resolveLede,
  resolvePotentialImpact,
  resolveRoiImpact,
  resolveSeenBefore,
  resolveSeverity,
} from './product-value';

/**
 * A draft, shaped as the customer-facing gap detail page reads it.
 *
 * The preview renders the REAL `GapDetail`, so the adapting has to happen here:
 * that component reads a published `Gap` inside a project, and a draft is
 * neither. Everything it needs and a draft cannot have — the vote tallies, the
 * comment list, the reviewer's own vote — is supplied empty rather than faked,
 * and the feedback row that would write them is not rendered at all
 * (`interactive={false}`).
 *
 * Field values come from the same three-level chain as the reader
 * (`product-value.ts`): the enriched `gap.*` field, else the run's equivalent,
 * else nothing. What the preview must NOT do is let a run fallback pass for
 * enriched output, so every substitution is reported back in `fromRun` and every
 * empty section in `missing`, and the banner above the page names them.
 */

/** One field the preview had to fill from somewhere other than `gap.*`. */
export interface PreviewNote {
  /** The `Gap` field, verbatim — this is a schema statement, not prose. */
  field: string;
  /** Where the rendered value came from, or who owes the absent one. */
  detail: string;
}

export interface DraftPreview {
  flatGap: { gap: DetailGap; project: DealIntelligenceProject };
  /** Rendered on the page, but sourced from the run rather than an agent. */
  fromRun: PreviewNote[];
  /** Not on the page at all: the app omits the section that would carry it. */
  missing: PreviewNote[];
}

/**
 * The run's lede as the app's `projectOverview` shape.
 *
 * The app's lede is an account narrative and a project narrative under their own
 * labels, which is exactly how the run carries them, so they map across one to
 * one. A narrative the run does not have becomes the empty string rather than
 * being invented — the app skips a blank label — and `draftToPreview` reports
 * the absence in `missing`.
 *
 * `gapSummary` is the gap statement, so it gets the resolved claim: the run's
 * own claim verbatim when no agent has written the enriched one yet.
 */
function ledeFromRun(draft: GapDraftView, claim: string): GapProjectOverview {
  return {
    account: draft.source.accountOverview ?? '',
    project: draft.source.projectOverview ?? '',
    gapSummary: claim,
  };
}

export function draftToPreview(draft: GapDraftView): DraftPreview {
  const fromRun: PreviewNote[] = [];
  const missing: PreviewNote[] = [];

  const claim = resolveClaim(draft);
  if (claim.from === 'run') {
    fromRun.push({
      field: 'projectOverview.gapSummary',
      detail: `the finder's claim verbatim, not the statement ${OVERVIEW_LABEL} will write`,
    });
  }

  const lede = resolveLede(draft);
  if (lede.from === 'run') {
    fromRun.push({
      field: 'projectOverview',
      detail: "the run's account and project narratives, joined",
    });
    // The app renders each narrative under its own label, so an absent one is a
    // section of the lede the reader simply will not see.
    for (const [fieldName, narrative] of [
      ['account', draft.source.accountOverview],
      ['project', draft.source.projectOverview],
    ] as const) {
      if (!narrative) {
        missing.push({
          field: `projectOverview.${fieldName}`,
          detail: `the run carries no ${fieldName} narrative either, so the label is omitted`,
        });
      }
    }
  }

  const fullProjectContext = resolveFullProjectContext(draft);
  if (fullProjectContext.from === 'run') {
    fromRun.push({
      field: 'fullProjectContext',
      detail: "the run's own background, copied at insert",
    });
  }

  const heading = titleOrigin(draft);
  if (heading === 'run') {
    fromRun.push({
      field: 'title',
      detail: `the finder's own heading; ${OVERVIEW_LABEL} owes the customer-facing one`,
    });
  } else if (heading === 'derived') {
    fromRun.push({
      field: 'title',
      detail: `derived from the first sentence of the claim; ${OVERVIEW_LABEL} owes a written one`,
    });
  }

  const severity = resolveSeverity(draft);
  if (severity.from === 'missing') {
    missing.push({
      field: 'severity',
      detail: `owed by ${ENRICHMENT_LABEL}, so the confidence readout says "not assigned" instead of banding a number`,
    });
  }

  const potentialImpact = resolvePotentialImpact(draft);
  if (potentialImpact.from === 'missing') {
    missing.push({
      field: 'potentialImpact',
      detail: 'the impact paragraph is omitted',
    });
  }

  const roiImpact = resolveRoiImpact(draft);
  if (roiImpact.from === 'missing') {
    missing.push({
      field: 'roiImpact',
      detail: 'no "Potential Money Loss" figure is rendered',
    });
  }

  const actionItems = resolveActionItems(draft);
  if (actionItems.from === 'missing') {
    missing.push({
      field: 'actionItems',
      detail: `the INTERA Suggestions card is omitted — owed by ${ENRICHMENT_LABEL}`,
    });
  }

  const escalation = resolveEscalation(draft);
  if (escalation.from === 'missing') {
    missing.push({
      field: 'escalation',
      detail: `no timeline — the run records a single drift point, not a history, so ${OVERVIEW_LABEL} has to build one`,
    });
  }

  const seenBefore = resolveSeenBefore(draft);
  if (seenBefore.from !== 'gap') {
    // The reader CAN show the run's precedents; the app cannot. `GapSeenBefore`
    // is a per-project story keyed to the enriched prose that links into it, and
    // a run precedent is a name and a sentence — so the preview says the section
    // is absent rather than filling it with something of a different shape.
    missing.push({
      field: 'seenBefore',
      detail:
        seenBefore.from === 'missing'
          ? 'no precedents on the run either'
          : "the run's precedents are a different shape — read them in the reader's Seen before section",
    });
  }

  const gap: DetailGap = {
    _id: draft._id,
    severity: severity.from === 'gap' ? severity.value : undefined,
    category: draft.variant,
    missability: draft.gap.missability,
    title: titleOf(draft),
    potentialImpact:
      potentialImpact.from === 'gap' ? potentialImpact.value : undefined,
    projectOverview:
      lede.from === 'gap'
        ? lede.projectOverview
        : ledeFromRun(draft, claim.text),
    roiImpact: roiImpact.from === 'gap' ? roiImpact.value : undefined,
    actionItems: actionItems.from === 'gap' ? actionItems.value : undefined,
    escalation: escalation.from === 'gap' ? escalation.value : undefined,
    seenBefore: seenBefore.from === 'gap' ? seenBefore.seenBefore : undefined,
    fullProjectContext: fullProjectContext.text,
    createdAt: draft.metadata.createdAt,
    // Not "zero votes so far": a draft cannot be voted on at all. The row that
    // would show these is not rendered.
    voteCounts: { relevant: 0, notRelevant: 0 },
    comments: [],
  };

  const externalRef = draft.gap.externalRef;

  return {
    flatGap: {
      gap,
      project: {
        externalId: draft.scopeExternalId,
        // The draft's own ref when the insert derived one — which it does for
        // every draft in the collection — else the scope, which is the same
        // record seen from the run side.
        entityType: externalRef?.entityType ?? 'unknown',
        source: externalRef?.source ?? 'unknown',
        name: draft.scopeName,
        gaps: [],
        maxSeverity: severity.from === 'gap' ? severity.value : 0,
      },
    },
    fromRun,
    missing,
  };
}
